<template>
  <v-dialog :model-value="isDialogActived" max-width="600px" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="valid" validate-on="lazy">
          <v-text-field
            label="Nombre de Usuario"
            v-model="editedItem.username"
            :disabled="isEdition && !canUpdate"
            :rules="[rules.requiredFieldRule]"/>
          <v-select label="Roles"
            v-model="editedItem.roleIds"
            item-title="displayName"
            item-value="id"
            multiple
            chips
            hint="Los permisos de todos los roles se suman"
            persistent-hint
            :disabled="isEdition && !canAssignRoles"
            :items="roleOptions"
            :rules="[rules.requiredFieldRule]"/>
          <v-select label="Compañia"
            v-model="editedItem.company"
            :item-title="companyTitle"
            item-value="id"
            return-object
            :disabled="isEdition && !canUpdate"
            :items="getBasicCompaniesInfo"
            :value-comparator="sameEntity"
            :rules="[rules.requiredFieldRule]"/>
          <v-checkbox v-if="(editedItem.id > 0) && canResetPassword"
            v-model="pswdCheckbox"
            label="Modificar Contraseñas?"/>
          <v-text-field v-if="(editedItem.id < 0) || pswdCheckbox"
            label="Contraseña"
            v-model="editedItem.password"
            type="password"
            :rules="[rules.requiredFieldRule , rules.min]"/>
          <v-textarea v-model="editedItem.reason" label="Motivo del cambio" rows="2" :rules="[rules.requiredFieldRule]" />
          <v-text-field v-if="(editedItem.id < 0) || pswdCheckbox"
            label="Repetir Contraseña"
            v-model="editedItem.passwordConfirmation"
            type="password"
            :rules="[rules.requiredFieldRule , rules.min]"/>
          <v-alert v-if="effectivePermissions.permissions.length" type="info" variant="tonal" class="mt-3">
            Esta cuenta tiene {{ effectivePermissions.permissions.length }} permisos efectivos.
            <details class="mt-2"><summary>Ver procedencia</summary>
              <div v-for="permission in effectivePermissions.permissions" :key="permission" class="text-caption mt-1">
                {{ permission }} — {{ permissionSources(permission) }}
              </div>
            </details>
          </v-alert>
          <v-alert v-if="operationError" type="error" variant="tonal" class="mt-3">{{ operationError }}</v-alert>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-alert
          v-if="pswrdsNotMatch"
          type="error"
          transition="scale-transition">
          Las contraseñas no coinciden.
        </v-alert>
        <v-spacer></v-spacer>
        <v-btn color="secondary" variant="text" @click="close()">Cancelar</v-btn>
        <v-btn color="primary" :loading="saving" @click="save()">Guardar</v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog v-model="previewOpen" max-width="620">
      <v-card>
        <v-card-title>Revisar reasignación de roles</v-card-title>
        <v-card-text>
          <p><strong>Permisos agregados:</strong> {{ preview.permissionsAdded?.join(', ') || 'Ninguno' }}</p>
          <p><strong>Permisos quitados:</strong> {{ preview.permissionsRemoved?.join(', ') || 'Ninguno' }}</p>
          <p><strong>Roles agregados:</strong> {{ roleNames(preview.rolesAdded) }}</p>
          <p><strong>Roles quitados:</strong> {{ roleNames(preview.rolesRemoved) }}</p>
          <v-alert v-if="preview.blockedReasons?.length" type="error" variant="tonal">La reasignación está bloqueada: {{ preview.blockedReasons.join(', ') }}</v-alert>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn variant="text" @click="previewOpen = false">Volver</v-btn><v-btn color="primary" :disabled="preview.blockedReasons?.length > 0" :loading="saving" @click="confirmSave">Confirmar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>
<script>
import { mapGetters } from 'vuex'
import userRestService from '../../services/user/UserRestService'
export default {
  name: 'AddEditUserForm',
  props: {
    formTitle: String
  },
  data: () => ({
    valid: true,
    pswrdsNotMatch: false,
    pswdCheckbox: false,
    saving: false,
    operationError: '',
    previewOpen: false,
    preview: {},
    effectivePermissions: { permissions: [], sources: {} },
    rules: {
      requiredFieldRule: v => !!v || 'Campo requerido',
      min: value => {
        if (value) {
          return (value.length >= 6 || '6 caracteres minimo')
        }
        return false
      }
    },
    editedItem: {
      id: -1,
      version: null,
      username: '',
      roleIds: [],
      roles: [],
      active: true,
      reason: '',
      company: {},
      password: '',
      passwordConfirmation: ''
    },
    defaultItem: {
      id: -1,
      version: null,
      username: '',
      roleIds: [],
      roles: [],
      active: true,
      reason: '',
      company: {},
      password: '',
      passwordConfirmation: ''
    }
  }),
  created () {
    this.getBasicCompanies()
  },
  computed: {
    ...mapGetters('userModule', ['isDialogActived', 'getEditedItem', 'getRoles']),
    ...mapGetters('authModule', ['hasPermission']),
    ...mapGetters('companyModule', ['getBasicCompaniesInfo']),
    currentItem: function () {
      return this.$store.getters['userModule/getEditedItem']
    },
    rePassword: function () {
      return this.editedItem.passwordConfirmation
    },
    itemId: function () {
      return this.editedItem.id
    },
    isEdition: function () { return this.editedItem.id > 0 },
    canUpdate: function () { return this.hasPermission('security.users.update') },
    canAssignRoles: function () { return this.hasPermission('security.users.assign_role') },
    canResetPassword: function () { return this.hasPermission('security.users.password_reset') },
    roleOptions: function () {
      const values = [...this.getRoles, ...(this.editedItem.roles || [])]
      return [...new Map(values.map(role => [Number(role.id), role])).values()]
    },
    getRoleDescription: function () { return 'Los permisos efectivos son la unión de los roles seleccionados.' }
  },

  watch: {
    currentItem: async function (item) {
      this.editedItem = Object.assign({}, this.defaultItem, item, {
        roleIds: [...(item.roleIds || [])],
        roles: [...(item.roles || [])],
        company: item.company ? Object.assign({}, item.company) : null
      })
      this.operationError = ''
      this.preview = {}
      this.effectivePermissions = { permissions: [], sources: {} }
      if (item.id > 0 && this.hasPermission('security.users.read')) {
        try { this.effectivePermissions = await userRestService.effectivePermissions(item.id) } catch (error) { this.operationError = this.errorMessage(error) }
      }
    },
    rePassword: function (value) {
      this.pswrdsNotMatch = (this.editedItem.password !== value)
    }
  },

  methods: {

    companyTitle (company) {
      return company && company.name ? company.name : ''
    },

    sameEntity (left, right) {
      return left && right && Number(left.id) === Number(right.id)
    },

    async getBasicCompanies () {
      await this.$store.dispatch('companyModule/getBasicCompaniesInfo')
    },

    setItem () {
      if (this.isEdition) {
        this.editedItem = this.$store.getters['userModule/getEditedItem']
      } else {
        this.editedItem = this.defaultItem
      }
    },

    close () {
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.valid = true
        this.pswrdsNotMatch = false
        this.pswdCheckbox = false
        this.previewOpen = false
        this.operationError = ''
        this.$refs.form.resetValidation()
        this.$store.dispatch('userModule/setDialogActived', false)
      }, 300)
    },

    async save () {
      const result = await this.$refs.form.validate()
      if (result.valid && this.arePswdEquals()) {
        this.operationError = ''
        if (!this.isEdition) return this.createUser()
        if (this.rolesChanged()) {
          this.saving = true
          try {
            this.preview = await userRestService.roleImpact(this.editedItem.id, {
              version: this.editedItem.version,
              roleIds: this.editedItem.roleIds,
              reason: this.editedItem.reason
            })
            this.previewOpen = true
          } catch (error) { this.operationError = this.errorMessage(error) } finally { this.saving = false }
          return
        }
        await this.confirmSave()
      }
    },
    async createUser () {
      this.saving = true
      try {
        await this.$store.dispatch('userModule/saveUser', this.editedItem)
        await this.$store.dispatch('userModule/getUsers')
        this.close()
      } catch (error) { this.operationError = this.errorMessage(error) } finally { this.saving = false }
    },
    async confirmSave () {
      this.saving = true
      this.operationError = ''
      try {
        let current = Object.assign({}, this.editedItem)
        if (this.metadataChanged()) current = await userRestService.updateMetadata(current)
        if (this.rolesChanged()) current = await userRestService.replaceRoles(current.id, {
          version: current.version,
          roleIds: this.editedItem.roleIds,
          reason: this.editedItem.reason
        })
        if (this.pswdCheckbox) current = await userRestService.resetPassword(current.id, {
          version: current.version,
          password: this.editedItem.password,
          passwordConfirmation: this.editedItem.passwordConfirmation,
          reason: this.editedItem.reason
        })
        this.previewOpen = false
        await this.$store.dispatch('userModule/getUsers')
        this.close()
      } catch (error) { this.previewOpen = false; this.operationError = this.errorMessage(error) } finally { this.saving = false }
    },
    rolesChanged () {
      const current = [...(this.currentItem.roleIds || [])].map(Number).sort((a, b) => a - b)
      const proposed = [...(this.editedItem.roleIds || [])].map(Number).sort((a, b) => a - b)
      return current.join(',') !== proposed.join(',')
    },
    metadataChanged () {
      return this.editedItem.username !== this.currentItem.username || Number(this.editedItem.company?.id) !== Number(this.currentItem.company?.id)
    },
    permissionSources (permission) { return (this.effectivePermissions.sources?.[permission] || []).map(role => role.displayName).join(', ') },
    roleNames (roles) { return (roles || []).map(role => role.displayName).join(', ') || 'Ninguno' },
    errorMessage (error) { return error.response?.data?.message || 'No se pudo completar la operación.' },
    arePswdEquals () {
      return (this.editedItem.password === this.editedItem.passwordConfirmation)
    }
  }
}
</script>
